import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { useBusinessAuth } from '../context/BusinessAuthContext';

const BEBAS = { fontFamily: "'Bebas Neue', sans-serif" };

export default function BusinessLoginPage() {
  const { t } = useTheme();
  const { supabaseConfigured, signInWithGoogle, signInWithOtp, verifyOtp } = useBusinessAuth();
  const [email, setEmail] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const cardStyle = { background: t.surface, border: `1px solid ${t.borderSubtle}`, borderRadius: '1rem' };
  const inputStyle = { background: t.inputBgNew, border: `1px solid ${t.inputBorderNew}`, color: t.textPrimary };

  const handleGoogle = async () => {
    setBusy(true);
    try {
      await signInWithGoogle();
    } catch (e) {
      toast.error(e?.message || 'Google sign-in failed');
      setBusy(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const { error } = await signInWithOtp(email);
      if (error) throw error;
      setOtpSent(true);
      toast.success('Check your email for a login code');
    } catch (e2) {
      toast.error(e2?.message || 'Could not send magic link');
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { error } = await verifyOtp(email, code);
      if (error) throw error;
      navigate(location.state?.from?.pathname || '/biz/orgs', { replace: true });
    } catch (e2) {
      toast.error(e2?.message || 'Invalid or expired code');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: t.pageBg, color: t.textPrimary, fontFamily: "'Barlow', sans-serif" }} data-testid="business-login-page">
      <div className="w-full max-w-md p-8" style={cardStyle}>
        <p className="text-[11px] tracking-[0.18em] uppercase font-semibold mb-2" style={{ color: t.textFaint }}>Divispend Business Workspace</p>
        <h1 className="text-4xl mb-6" style={{ ...BEBAS }}>Sign in</h1>

        {!supabaseConfigured && (
          <p className="text-sm mb-4" style={{ color: '#F59E0B' }} data-testid="business-login-unconfigured">
            Supabase isn't configured in this environment yet — sign-in is unavailable until setup is complete.
          </p>
        )}

        <button
          onClick={handleGoogle}
          disabled={busy || !supabaseConfigured}
          className="w-full h-11 rounded-xl font-semibold text-sm mb-5 disabled:opacity-50"
          style={{ background: t.surfaceAlt, border: `1px solid ${t.dividerBorder}`, color: t.textPrimary }}
          data-testid="business-google-signin-button"
        >
          Continue with Google
        </button>

        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px" style={{ background: t.dividerBorder }} />
          <span className="text-xs" style={{ color: t.textFaint }}>or</span>
          <div className="flex-1 h-px" style={{ background: t.dividerBorder }} />
        </div>

        {!otpSent ? (
          <form onSubmit={handleSendOtp} className="space-y-3">
            <input
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy || !supabaseConfigured}
              className="w-full h-11 rounded-xl px-4 text-sm disabled:opacity-50"
              style={inputStyle}
              data-testid="business-login-email-input"
              required
            />
            <button
              type="submit"
              disabled={busy || !supabaseConfigured}
              className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-50"
              style={{ background: t.accent }}
              data-testid="business-login-send-otp-button"
            >
              Email me a login code
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOtp} className="space-y-3">
            <p className="text-xs" style={{ color: t.textMutedNew }}>Enter the 6-digit code sent to {email}</p>
            <input
              type="text"
              placeholder="123456"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full h-11 rounded-xl px-4 text-sm tracking-widest text-center"
              style={inputStyle}
              data-testid="business-login-otp-input"
              required
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full h-11 rounded-xl font-bold text-black text-sm disabled:opacity-50"
              style={{ background: t.accent }}
              data-testid="business-login-verify-otp-button"
            >
              Verify &amp; sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
